import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ConfirmModal from 'features/portfolio/components/ConfirmModal'
import { SkeletonRows } from 'components/Skeleton'
import { getBpAliases, addBpAlias, removeBpAlias } from 'services/BpService'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'

const BuildingNamesModal = ({ bpId, bpName, canManage, onClose, onChanged }) => {
  const [aliases, setAliases] = useState([])
  const [loading, setLoading] = useState(true)
  const [value, setValue] = useState('')
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [saving, setSaving] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    getBpAliases({ bpId })
      .then(res => setAliases(res?.data?.data || []))
      .catch(() => setAliases([]))
      .finally(() => setLoading(false))
  }, [bpId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async e => {
    e.preventDefault()
    const name = value.trim()
    if (!name) return
    setSaving(true)
    setError(null)
    setWarning(null)
    try {
      const res = await addBpAlias({ bpId, name })
      const added = res?.data?.data
      setValue('')
      setWarning(added?.warning || null)
      toast.success(`"${name}" added.`)
      load()
      if (onChanged) onChanged()
    } catch (err) {
      setError(err?.response?.data?.message || 'That alias could not be added. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const confirmRemove = async () => {
    const target = pendingRemoval
    setPendingRemoval(null)
    if (!target) return
    try {
      await removeBpAlias({ bpId, aliasId: target.id })
      setWarning(null)
      setError(null)
      toast.success(`"${target.name}" removed.`)
      load()
      if (onChanged) onChanged()
    } catch {
      toast.error('That alias could not be removed. Please try again.')
    }
  }

  return createPortal(
    <>
      <Overlay onMouseDown={onClose}>
        <Card onMouseDown={e => e.stopPropagation()} role='dialog' aria-modal='true' aria-label={`Aliases for ${bpName}`}>
          <Head>
            <div>
              <Title>Aliases for this building</Title>
              <Lead>
                Documents do not always use the name {bpName}. Add aliases, the other names people use for it, such as a
                block reference or an older project name, and this building will be recognised when they appear.
              </Lead>
            </div>
            <CloseBtn type='button' onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 20 }} /></CloseBtn>
          </Head>

          <Explainer>
            <ExplainerTitle>What adding an alias does</ExplainerTitle>
            <ExplainerList>
              <li>A document that uses the alias is recognised as belonging here, instead of being marked as filed against the wrong building.</li>
              <li>Searching for the alias finds this building, and finds its documents.</li>
              <li>Guru treats the aliases as the same building when you ask it questions.</li>
            </ExplainerList>
          </Explainer>

          <SectionTitle>Aliases</SectionTitle>
          {loading ? (
            <SkeletonRows rows={3} gap={8} widths={['100%', '100%', '100%']} />
          ) : (
            <List>
              <Row key='canonical'>
                <RowName>{bpName}</RowName>
                <RowTag>The building's name</RowTag>
              </Row>
              {aliases.map(alias => (
                <Row key={alias.id}>
                  <RowName>{alias.name}</RowName>
                  {canManage && (
                    <RemoveBtn type='button' onClick={() => setPendingRemoval(alias)} title={`Remove ${alias.name}`}>
                      <DeleteOutlineRoundedIcon style={{ fontSize: 17 }} /> Remove
                    </RemoveBtn>
                  )}
                </Row>
              ))}
              {aliases.length === 0 && (
                <Empty>No aliases yet. Add one if your documents call this building something different.</Empty>
              )}
            </List>
          )}

          {canManage && (
            <Form onSubmit={submit}>
              <FieldLabel htmlFor='bp-alias-input'>Add an alias</FieldLabel>
              <FieldRow>
                <Input
                  id='bp-alias-input'
                  data-tour-id='buildingNames.input'
                  value={value}
                  placeholder='An alias for this building'
                  maxLength={255}
                  onChange={e => { setValue(e.target.value); setError(null) }}
                  aria-describedby='bp-alias-help'
                />
                <AddBtn type='submit' data-tour-id='buildingNames.submit' disabled={saving || !value.trim()}>{saving ? 'Adding…' : 'Add alias'}</AddBtn>
              </FieldRow>
              <Help id='bp-alias-help'>At least three letters or numbers, so it does not match unrelated documents.</Help>
              {error && <ErrorText role='alert'>{error}</ErrorText>}
              {warning && (
                <WarningBox role='status'>
                  <WarningAmberRoundedIcon style={{ fontSize: 18, flexShrink: 0 }} />
                  <span>{warning}</span>
                </WarningBox>
              )}
            </Form>
          )}
        </Card>
      </Overlay>

      {pendingRemoval && (
        <ConfirmLayer>
          <ConfirmModal
            title={`Remove the alias "${pendingRemoval.name}"?`}
            message={`Documents that use "${pendingRemoval.name}" will no longer be recognised as belonging to this building, and searching for it will not find this building. Nothing already filed here will be moved.`}
            confirmLabel='Remove name'
            onConfirm={confirmRemove}
            onClose={() => setPendingRemoval(null)}
          />
        </ConfirmLayer>
      )}
    </>,
    document.body,
  )
}

export default BuildingNamesModal

const ConfirmLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100030;
`

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 100010; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 16px;
`
const Card = styled.div`
  width: 100%; max-width: 560px; max-height: 88vh; overflow: auto; background: ${surface.card};
  border: 1px solid ${line.base}; border-radius: ${radius.md}; box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  padding: 20px 22px 22px;
  @media (max-width: ${bpToken.mobile}) { padding: 16px; max-height: 92vh; }
`
const Head = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;`
const Title = styled.h3`margin: 0; font-size: 17px; font-weight: 700; color: ${ink.base};`
const Lead = styled.p`margin: 6px 0 0; font-size: ${font.body}; line-height: 1.5; color: ${ink.soft};`
const CloseBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px;
  border: none; background: transparent; color: ${ink.mute}; border-radius: ${radius.sm}; cursor: pointer; transition: ${transition};
  &:hover { background: ${surface.wash}; color: ${ink.base}; }
`
const Explainer = styled.div`
  margin: 16px 0 4px; padding: 12px 14px; background: ${surface.wash};
  border: 1px solid ${line.base}; border-radius: ${radius.sm};
`
const ExplainerTitle = styled.div`font-size: ${font.small}; font-weight: 700; color: ${ink.base}; margin-bottom: 6px;`
const ExplainerList = styled.ul`
  margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px;
  li { font-size: ${font.small}; line-height: 1.5; color: ${ink.soft}; }
`
const SectionTitle = styled.div`
  margin: 18px 0 8px; font-size: ${font.caption}; font-weight: 800; letter-spacing: 0.05em;
  text-transform: uppercase; color: ${ink.mute};
`
const List = styled.div`display: flex; flex-direction: column; gap: 6px;`
const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px;
  padding: 8px 12px; border: 1px solid ${line.base}; border-radius: ${radius.sm}; background: ${surface.card};
`
const RowName = styled.span`font-size: ${font.body}; font-weight: 600; color: ${ink.base}; min-width: 0; overflow-wrap: anywhere;`
const RowTag = styled.span`font-size: ${font.caption}; color: ${ink.mute}; white-space: nowrap;`
const RemoveBtn = styled.button`
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 5px; min-height: 40px; padding: 8px 12px;
  border: 1px solid ${line.strong}; border-radius: ${radius.sm}; background: ${surface.card};
  color: #b00020; font-size: 13px; font-weight: 700; cursor: pointer; transition: ${transition};
  &:hover { border-color: #b00020; background: #fdecea; }
`
const Empty = styled.div`font-size: ${font.body}; color: ${ink.mute}; padding: 10px 2px; line-height: 1.5;`
const Form = styled.form`margin-top: 18px; padding-top: 16px; border-top: 1px solid ${line.base};`
const FieldLabel = styled.label`display: block; font-size: ${font.body}; font-weight: 700; color: ${ink.base}; margin-bottom: 6px;`
const FieldRow = styled.div`
  display: flex; gap: 8px; align-items: stretch;
  @media (max-width: ${bpToken.mobile}) { flex-direction: column; }
`
const Input = styled.input`
  flex: 1 1 auto; min-width: 0; min-height: 44px; border: 1px solid ${line.strong}; border-radius: ${radius.sm};
  padding: 10px 12px; font-size: 16px; color: ${ink.base}; transition: ${transition};
  &::placeholder { color: ${ink.mute}; }
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.secondary}; }
`
const AddBtn = styled.button`
  flex-shrink: 0; min-height: 44px; padding: 10px 18px; border: none; border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.secondary}; color: #fff; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: ${transition};
  &:disabled { opacity: 0.5; cursor: default; }
  &:not(:disabled):hover { background: #2aa6b3; }
`
const Help = styled.div`margin-top: 6px; font-size: ${font.small}; color: ${ink.mute};`
const ErrorText = styled.div`margin-top: 8px; font-size: ${font.body}; font-weight: 600; color: #b00020;`
const WarningBox = styled.div`
  margin-top: 10px; display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px;
  background: #fef3c7; border: 1px solid #fde68a; border-radius: ${radius.sm};
  font-size: ${font.small}; line-height: 1.5; color: #92400e;
`
